import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Download, Rocket, Compass, Wand2, MessageCircle,
  Upload, FolderKanban, Lightbulb, Zap, Package, Eye, Check,
  BookOpen, Sparkles, Search, X, CornerDownLeft,
} from 'lucide-react';

const BRAND = {
  navy: '#03183D',
  deepBlue: '#0A2E5D',
  teal: '#16AAA8',
  cream: '#F7F9FC',
  amber: '#F5A623',
};

const GUIDE_PDF = '/guides/creataby-first-project-10-minutes.pdf';

// Flat, beginner-friendly search index. Each entry deep-links to a section.
const SEARCH_INDEX = [
  { section: 'getting-started', title: 'How do I sign up?', snippet: 'Enter your email and click the magic link — no password needed.', keywords: 'sign up signup register account email magic link login start free create account get started' },
  { section: 'getting-started', title: 'Do I need a credit card to start?', snippet: 'No. The Free plan lets you start with 2 generations a month, no card required.', keywords: 'credit card free plan cost price start no card payment trial' },
  { section: 'choose-path', title: 'Which income path should I pick?', snippet: 'Fast Cash, Digital Product, or Faceless Affiliate — or let Creataby recommend one.', keywords: 'path fast cash digital product faceless affiliate choose pick which recommend quiz not sure income' },
  { section: 'choose-path', title: 'What is the Fast Cash path?', snippet: 'Package a skill into a simple service and land your first paying client this week.', keywords: 'fast cash service client freelance skill sell quick offer outreach' },
  { section: 'choose-path', title: 'What is a Digital Product?', snippet: 'Turn your knowledge into an ebook, workbook, or template you can sell repeatedly.', keywords: 'digital product ebook workbook template sell knowledge course' },
  { section: 'guided-steps', title: 'How does the "Guide Me" button work?', snippet: 'Type your idea in plain words and Creataby writes the copy, outline, or script.', keywords: 'guide me generate ai write button generation how steps work mission input' },
  { section: 'guided-steps', title: 'How do I save or redo an output?', snippet: 'Press "Save All to Workspace" to keep it, or "Redo" for a fresh version.', keywords: 'save redo regenerate workspace output keep edit tweak mark complete' },
  { section: 'reggie', title: 'Who is Reggie / how do I get help on a step?', snippet: 'Tap "Ask Reggie" on any screen for an explanation or a suggested answer.', keywords: 'reggie assistant help chatbot ask stuck explanation suggestion use this support' },
  { section: 'exporting', title: 'How do I export my work?', snippet: 'Export to PDF, Stan Store, Gumroad, Systeme.io, Carrd, or ClickFunnels in one click.', keywords: 'export pdf download stan store gumroad systeme carrd clickfunnels funnel share publish' },
  { section: 'projects-plans', title: 'How many projects can I have?', snippet: 'Free: 1 active. Creator: up to 3. Creator Pro: unlimited. Archive to free a slot.', keywords: 'projects active archive limit how many plan free creator pro reactivate slot' },
  { section: 'projects-plans', title: 'What are the generation limits?', snippet: 'Free gives 2 generations a month; Creator and Creator Pro are unlimited.', keywords: 'generations limit month free unlimited creator pro usage cap how many' },
  { section: 'projects-plans', title: 'How do I upgrade or change my plan?', snippet: 'Head to the pricing page to compare plans and upgrade anytime.', keywords: 'upgrade change plan pricing billing subscribe cancel downgrade payment' },
  { section: 'tips', title: 'How do I get the best results?', snippet: 'Be specific, write like you talk, and save a "good enough" version first.', keywords: 'tips best results specific quality improve better prompt advice' },
];

const SECTIONS = [
  { id: 'getting-started', label: 'Getting started', icon: Rocket },
  { id: 'choose-path', label: 'Choose your path', icon: Compass },
  { id: 'guided-steps', label: 'Using guided steps', icon: Wand2 },
  { id: 'reggie', label: 'Meet Reggie', icon: MessageCircle },
  { id: 'exporting', label: 'Exporting your work', icon: Upload },
  { id: 'projects-plans', label: 'Projects & plans', icon: FolderKanban },
  { id: 'tips', label: 'Tips for best results', icon: Lightbulb },
];

const PATHS = [
  { icon: Zap, tint: 'from-emerald-500 to-teal-600', name: 'Fast Cash', body: 'Package a skill into a simple service and land your first paying client this week using ready-to-send outreach scripts.' },
  { icon: Package, tint: 'from-violet-500 to-indigo-600', name: 'Digital Product', body: 'Turn what you know into an ebook, workbook, or template — with an outline, cover concept, sales page, and 5-email launch sequence.' },
  { icon: Eye, tint: 'from-amber-500 to-orange-600', name: 'Faceless Affiliate', body: 'Recommend proven offers and build a bridge page that quietly sends buyers your way — no camera, no face required.' },
];

const TIPS = [
  { title: 'Be specific', body: '"Busy parents who want quick dinners" beats "everyone." The more real your input, the sharper your result.' },
  { title: 'Write like you talk', body: 'Use plain language, as if you were texting a friend. Creataby handles the polish for you.' },
  { title: 'Save "good enough" now', body: "Don't chase perfect on the first pass. Save a solid version and refine it later — momentum beats perfection." },
  { title: 'Lean on Reggie', body: 'Stuck on any step? Ask Reggie for an explanation or a suggested answer you can drop straight in.' },
  { title: 'Use Redo freely', body: 'Not loving an output? Press Redo for a fresh angle. Every generation is a new starting point, not a locked answer.' },
  { title: 'Finish one path first', body: 'Complete a single project end to end before starting another. A finished asset beats three half-built ideas.' },
];

const StepRow = ({ n, title, children }) => (
  <div className="flex gap-4">
    <div
      className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-sm"
      style={{ backgroundColor: BRAND.teal }}
    >
      {n}
    </div>
    <div className="pt-0.5">
      <h4 className="font-bold text-base mb-1" style={{ color: BRAND.deepBlue }}>{title}</h4>
      <p className="text-sm text-slate-600 leading-relaxed">{children}</p>
    </div>
  </div>
);

const Section = ({ id, icon: Icon, eyebrow, title, children }) => (
  <section id={id} className="scroll-mt-24" data-testid={`docs-section-${id}`}>
    <div className="flex items-center gap-2 mb-2">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(22,170,168,0.12)' }}>
        <Icon className="h-4 w-4" style={{ color: BRAND.teal }} />
      </div>
      <span className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: BRAND.teal }}>{eyebrow}</span>
    </div>
    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4" style={{ color: BRAND.deepBlue }}>{title}</h2>
    <div className="space-y-4">{children}</div>
  </section>
);

const Docs = () => {
  const { session, isOnboarded, loading } = useAuth();
  const [active, setActive] = useState('getting-started');
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const results = q
    ? SEARCH_INDEX.filter(
        (e) =>
          e.title.toLowerCase().includes(q) ||
          e.snippet.toLowerCase().includes(q) ||
          e.keywords.toLowerCase().includes(q)
      ).slice(0, 6)
    : [];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Authenticated users can still read the docs; we don't force-redirect here
  // so it works from both the marketing site and inside the app.
  if (!loading && session === undefined) {
    return <Navigate to="/" replace />;
  }

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleResultClick = (id) => {
    setQuery('');
    // let the results panel unmount before scrolling
    setTimeout(() => scrollTo(id), 20);
  };

  const ctaHref = session ? (isOnboarded ? '/dashboard' : '/onboarding') : '/login';

  return (
    <div className="min-h-screen bg-white" style={{ color: BRAND.deepBlue }}>
      {/* Top Nav */}
      <header
        className="sticky top-0 z-40 border-b border-slate-100 backdrop-blur-md"
        style={{ backgroundColor: 'rgba(255,255,255,0.85)' }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="docs-nav-logo">
            <img src="/banner-logo.png" alt="Creataby" className="h-10" />
          </Link>
          <nav className="flex items-center gap-1 sm:gap-3">
            <Link to="/pricing">
              <Button variant="ghost" size="sm" className="text-sm text-slate-600 hover:text-slate-900" data-testid="docs-nav-pricing">
                Pricing
              </Button>
            </Link>
            <Link to={ctaHref}>
              <Button
                size="sm"
                className="text-sm font-semibold rounded-full px-4 text-white shadow-[0_2px_10px_rgba(22,170,168,0.35)]"
                style={{ backgroundColor: BRAND.teal }}
                data-testid="docs-nav-cta"
              >
                {session ? 'Open app' : 'Start free'} <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-100">
        <div
          aria-hidden
          className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-25"
          style={{ background: 'radial-gradient(closest-side, #16AAA8, transparent)' }}
        />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-12 relative">
          <span
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full mb-5"
            style={{ backgroundColor: 'rgba(22,170,168,0.10)', color: BRAND.teal }}
            data-testid="docs-hero-badge"
          >
            <BookOpen className="h-3.5 w-3.5" />
            Help Center & Guides
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold leading-[1.05] tracking-tight mb-4 max-w-2xl" style={{ color: BRAND.deepBlue }} data-testid="docs-hero-title">
            Everything you need to go from idea to launch.
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mb-6 leading-relaxed">
            Short, friendly guides that show you exactly how Creataby works — no jargon, no fluff. Start with the 10-minute quickstart below.
          </p>

          {/* Search */}
          <div className="relative max-w-xl mb-8" data-testid="docs-search">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the guides — try “export” or “upgrade”"
                className="w-full h-12 pl-11 pr-10 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 shadow-[0_4px_18px_rgba(3,24,61,0.06)] focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-400 transition-all"
                data-testid="docs-search-input"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label="Clear search"
                  data-testid="docs-search-clear"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Results dropdown */}
            {q && (
              <div
                className="absolute z-30 mt-2 w-full rounded-xl border border-slate-100 bg-white shadow-[0_16px_44px_rgba(3,24,61,0.16)] overflow-hidden"
                data-testid="docs-search-results"
              >
                {results.length > 0 ? (
                  <ul className="max-h-[340px] overflow-y-auto py-1">
                    {results.map((r) => {
                      const sec = SECTIONS.find((s) => s.id === r.section);
                      const Icon = sec ? sec.icon : Search;
                      return (
                        <li key={r.title}>
                          <button
                            onClick={() => handleResultClick(r.section)}
                            className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
                            data-testid={`docs-search-result-${r.section}`}
                          >
                            <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(22,170,168,0.10)' }}>
                              <Icon className="h-4 w-4" style={{ color: BRAND.teal }} />
                            </span>
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold text-slate-800 group-hover:text-slate-900">{r.title}</span>
                              <span className="block text-xs text-slate-500 truncate">{r.snippet}</span>
                            </span>
                            <CornerDownLeft className="h-3.5 w-3.5 text-slate-300 ml-auto mt-1 flex-shrink-0" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="px-4 py-6 text-center" data-testid="docs-search-empty">
                    <p className="text-sm text-slate-600 font-medium">No results for “{query}”.</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Try a different word, or email{' '}
                      <a href="mailto:support@creataby.com" className="underline" style={{ color: BRAND.teal }}>support@creataby.com</a>.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Download CTA */}
          <motion.a
            href={GUIDE_PDF}
            download
            whileHover={{ y: -2 }}
            className="inline-flex items-center gap-4 rounded-2xl p-5 pr-6 text-white shadow-[0_12px_40px_rgba(3,24,61,0.25)] transition-all"
            style={{ backgroundColor: BRAND.navy }}
            data-testid="docs-download-guide"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(22,170,168,0.2)' }}>
              <Download className="h-5 w-5" style={{ color: BRAND.teal }} />
            </div>
            <div className="text-left">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: '#7FE3E1' }}>Free PDF · Downloadable</p>
              <p className="text-base font-bold text-white">Your First Project in 10 Minutes</p>
            </div>
            <ArrowRight className="h-5 w-5 ml-2 flex-shrink-0" style={{ color: BRAND.teal }} />
          </motion.a>
        </div>
      </section>

      {/* BODY: sticky TOC + content */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-[240px_1fr] gap-10 lg:gap-16">
        {/* Sticky TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400 mb-3">On this page</p>
            <nav className="space-y-1" data-testid="docs-toc">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const isActive = active === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={`w-full flex items-center gap-2.5 text-left text-sm px-3 py-2 rounded-lg transition-all ${
                      isActive ? 'font-semibold' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                    style={isActive ? { backgroundColor: 'rgba(22,170,168,0.10)', color: BRAND.teal } : {}}
                    data-testid={`docs-toc-${s.id}`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    {s.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="space-y-16 min-w-0">
          <Section id="getting-started" icon={Rocket} eyebrow="Getting started" title="Create your account & first project">
            <p className="text-slate-600 leading-relaxed">
              Creataby is your guided workspace for turning what you know into something you can sell. You'll pick a path, follow a few simple steps, and let the app write the copy, outlines, and scripts for you. Here's how to begin:
            </p>
            <div className="space-y-4 mt-4">
              <StepRow n="1" title="Sign up with your email">
                Enter your email and we'll send a magic link — no password to remember. Click it and you're in. The Free plan lets you start with no credit card.
              </StepRow>
              <StepRow n="2" title="Answer a few quick questions">
                A short 60-second setup helps Creataby recommend the best path for your goals and experience level.
              </StepRow>
              <StepRow n="3" title="Land on your dashboard">
                Your path opens as a clear checklist. The dashboard always shows your next step, so you never face a blank page.
              </StepRow>
            </div>
            <div className="rounded-xl p-5 mt-4 border" style={{ backgroundColor: 'rgba(22,170,168,0.06)', borderColor: 'rgba(22,170,168,0.2)' }}>
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: BRAND.teal }} />
                <p className="text-sm text-slate-700">
                  <b>New here?</b> Download the <a href={GUIDE_PDF} download className="font-semibold underline" style={{ color: BRAND.teal }} data-testid="docs-inline-download">10-minute quickstart guide</a> and follow along — you'll finish your first asset before your coffee gets cold.
                </p>
              </div>
            </div>
          </Section>

          <Section id="choose-path" icon={Compass} eyebrow="Choose your path" title="Which path is right for you?">
            <p className="text-slate-600 leading-relaxed">
              Every plan includes all three income paths. Not sure which to pick? Type your situation into the "Not sure?" box during setup and Creataby recommends the fastest path to your first result.
            </p>
            <div className="grid sm:grid-cols-1 gap-4 mt-2">
              {PATHS.map((p) => {
                const Icon = p.icon;
                return (
                  <div key={p.name} className="flex gap-4 rounded-xl border border-slate-100 p-5 shadow-[0_4px_18px_rgba(3,24,61,0.05)]" data-testid={`docs-path-${p.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${p.tint} flex items-center justify-center flex-shrink-0 shadow-md`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-base mb-1" style={{ color: BRAND.deepBlue }}>{p.name}</h4>
                      <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          <Section id="guided-steps" icon={Wand2} eyebrow="Using guided steps" title="How each step works">
            <p className="text-slate-600 leading-relaxed">
              Creataby breaks your project into small steps with one clear action each. Here's the rhythm you'll repeat:
            </p>
            <div className="space-y-4 mt-4">
              <StepRow n="1" title="Read your mission">
                Each step opens with a short "Your Mission" card explaining what you're creating and why it matters.
              </StepRow>
              <StepRow n="2" title="Fill in the box & press Guide Me">
                Type a sentence or two about your idea in plain words. Press <b>Guide Me</b> and Creataby writes the copy, outline, or script in seconds.
              </StepRow>
              <StepRow n="3" title="Review, tweak & save">
                Like it? Press <b>Save All to Workspace</b>. Want a different angle? Press <b>Redo</b> for a fresh version. Edit anything you like.
              </StepRow>
              <StepRow n="4" title="Mark complete">
                Press <b>Mark Complete</b> to lock in your progress and preview your next step. Your project builds itself, one step at a time.
              </StepRow>
            </div>
          </Section>

          <Section id="reggie" icon={MessageCircle} eyebrow="Meet Reggie" title="Your always-on assistant">
            <p className="text-slate-600 leading-relaxed">
              Reggie is the friendly assistant available on every screen via the <b>"Ask Reggie"</b> button. Reggie explains what a step is really asking, gives you thinking prompts, and even suggests a recommended answer you can drop straight in.
            </p>
            <ul className="space-y-2.5 mt-2">
              {[
                'Ask "what does this step mean?" for a plain-English explanation',
                'Get a recommended answer, then press "Use This" to insert it',
                'Press "Regenerate" for a different variation',
                'Reggie remembers your conversation for the current session',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: BRAND.teal }} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section id="exporting" icon={Upload} eyebrow="Exporting your work" title="Take your assets anywhere">
            <p className="text-slate-600 leading-relaxed">
              When you're happy with what you've built, export it with one click from the <b>Export</b> menu. Creataby formats your content for the tools you already use:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
              {['PDF', 'Plain text (.txt)', 'Markdown (.md)', 'Stan Store', 'Gumroad', 'Systeme.io', 'Carrd', 'ClickFunnels'].map((tool) => (
                <div key={tool} className="rounded-lg border border-slate-100 px-4 py-3 text-sm font-medium text-slate-700 bg-slate-50/50" data-testid={`docs-export-${tool.toLowerCase().replace(/[^a-z]/g, '')}`}>
                  {tool}
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-3">
              Advanced exports like ClickFunnels &amp; Systeme.io are available on Creator Pro.
            </p>
          </Section>

          <Section id="projects-plans" icon={FolderKanban} eyebrow="Projects & plans" title="Managing projects and limits">
            <p className="text-slate-600 leading-relaxed">
              Everything you build lives in <b>My Projects</b>, where you can track progress, archive finished work, and reactivate projects later. Your plan controls how many projects can be active at once and how many generations you get.
            </p>
            <div className="overflow-hidden rounded-xl border border-slate-100 mt-2">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: BRAND.cream }}>
                    <th className="text-left px-4 py-3 font-semibold" style={{ color: BRAND.deepBlue }}>Plan</th>
                    <th className="text-left px-4 py-3 font-semibold" style={{ color: BRAND.deepBlue }}>Generations</th>
                    <th className="text-left px-4 py-3 font-semibold" style={{ color: BRAND.deepBlue }}>Active projects</th>
                  </tr>
                </thead>
                <tbody className="text-slate-600">
                  <tr className="border-t border-slate-100"><td className="px-4 py-3 font-medium">Free</td><td className="px-4 py-3">2 / month</td><td className="px-4 py-3">1</td></tr>
                  <tr className="border-t border-slate-100"><td className="px-4 py-3 font-medium">Creator</td><td className="px-4 py-3">Unlimited</td><td className="px-4 py-3">Up to 3</td></tr>
                  <tr className="border-t border-slate-100"><td className="px-4 py-3 font-medium">Creator Pro</td><td className="px-4 py-3">Unlimited</td><td className="px-4 py-3">Unlimited</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-slate-600 mt-3">
              Hit your limit? <b>Archive</b> a project to free up a slot, or{' '}
              <Link to="/pricing" className="font-semibold underline" style={{ color: BRAND.teal }} data-testid="docs-pricing-link">upgrade your plan</Link> for more room. Archived work is never deleted — reactivate it any time.
            </p>
          </Section>

          <Section id="tips" icon={Lightbulb} eyebrow="Tips for best results" title="Get more out of Creataby">
            <div className="grid sm:grid-cols-2 gap-4">
              {TIPS.map((t) => (
                <div key={t.title} className="rounded-xl border border-slate-100 p-5 shadow-[0_4px_18px_rgba(3,24,61,0.05)]" data-testid={`docs-tip-${t.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <h4 className="font-bold text-base mb-1.5 flex items-center gap-2" style={{ color: BRAND.deepBlue }}>
                    <Lightbulb className="h-4 w-4" style={{ color: BRAND.amber }} />
                    {t.title}
                  </h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{t.body}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* Bottom CTA */}
          <div className="rounded-2xl overflow-hidden relative" style={{ backgroundColor: BRAND.navy }}>
            <div aria-hidden className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(60% 60% at 50% 40%, rgba(22,170,168,0.35), transparent 70%)' }} />
            <div className="relative px-8 py-10 text-center">
              <h3 className="text-2xl font-bold text-white mb-2">Ready to build your first project?</h3>
              <p className="text-slate-300 mb-6">Start free — no credit card, no risk. Your idea is waiting.</p>
              <Link to={ctaHref}>
                <Button
                  size="lg"
                  className="text-base font-semibold rounded-full px-8 h-12 text-white shadow-[0_6px_24px_rgba(22,170,168,0.6)] hover:-translate-y-0.5 transition-all"
                  style={{ backgroundColor: BRAND.teal }}
                  data-testid="docs-bottom-cta"
                >
                  {session ? 'Open Creataby' : 'Start free'} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>

      {/* FOOTER */}
      <footer className="border-t border-slate-100 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/banner-logo.png" alt="Creataby" className="h-8" />
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/pricing" className="hover:text-slate-900">Pricing</Link>
            <Link to="/docs" className="hover:text-slate-900">Guides</Link>
            <a href="mailto:support@creataby.com" className="hover:text-slate-900">Contact</a>
          </div>
          <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} Creataby. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Docs;
